import React, {Fragment, useCallback, useEffect} from "react";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {deleteUrlsModalState, setIsOpened} from "./DeleteUrlsModalSlice";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import {createCacheClearJob} from "../DeleteUrlsSlice";
import {clearListResult, list, urlsState as urState} from "../../UrlsSlice";
import {SliceStatus} from "../../../BaseState";
import {AdaptiveType} from "../../UrlsState";
import {deleteUrlsState as dUrlsState} from "../DeleteUrlsSlice";
import {CacheClearJobStatus} from "../DeleteUrlsState";

interface DeleteUrlsProps {
  readonly userId: number;
  readonly like?: string;
  readonly regex?: string;
  readonly adaptiveType?: AdaptiveType;
}

const DeleteUrlsModal = (props: DeleteUrlsProps) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(deleteUrlsModalState);
  const urlsState = useAppSelector(urState);
  const deleteUrlsState = useAppSelector(dUrlsState);

  const {userId, like, regex, adaptiveType} = props;

  const listUrls = useCallback(() => {
    dispatch(
      list({
        userId,
        like,
        regex,
        adaptiveType,
        limit: 10,
        offset: 0
      })
    );
  }, [adaptiveType, dispatch, like, regex, userId]);

  useEffect(() => {
    if (state.isOpened && deleteUrlsState.cacheClearJobStatus === CacheClearJobStatus.idle) {
      listUrls();
    }
  }, [deleteUrlsState.cacheClearJobStatus, listUrls, state.isOpened]);

  const handleClose = () => {
    dispatch(clearListResult());
    dispatch(setIsOpened(false));
  };

  const handleClearCache = () => {
    dispatch(
      createCacheClearJob({
        userId: userId,
        filter: like,
        regex: regex,
        adaptiveType: adaptiveType === AdaptiveType.all ? undefined : adaptiveType
      })
    ).then(() => {
      dispatch(clearListResult());
    });
  };

  const command = `select * from cached_urls where user_id = ${userId} ${like ? `and url like '${like}'` : ""} ${
    regex ? `and url ~ '${regex}'` : ""
  } ${!regex && !like ? "and url like '%'" : ""} `;
  const hasUrl = !!urlsState.result?.count;
  const disabled =
    deleteUrlsState.status !== SliceStatus.idle || !hasUrl || deleteUrlsState.cacheClearJobStatus !== CacheClearJobStatus.idle;
  const loading = !urlsState.result;
  const clearingCache = deleteUrlsState.cacheClearJobStatus === CacheClearJobStatus.processing;

  return (
    <Modal show={state.isOpened} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Clear Cache</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="container">
          {loading && !clearingCache && <div className="row align-items-center">Loading...</div>}
          {clearingCache && (
            <div className="row align-items-center">
              Job queued. You can close this modal/tab or refresh or whatever. The cache clear button becomes available again once the job
              finished.
            </div>
          )}
          {!loading && !clearingCache && (
            <Fragment>
              <div className="row align-items-center pb-3">The command that will run for selecting urls: {command}</div>
              <div className="row align-items-center pb-3">This will delete: {urlsState.result.count} url(s)</div>
              {hasUrl && (
                <div className="row align-items-center pb-3">
                  Like:
                  <div className="col">
                    {urlsState.result.urls.map((url) => {
                      return <p key={url.url}>{url.url}</p>;
                    })}
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button className={`btn ${deleteUrlsState.error ? "btn-danger" : "btn-primary"}`} onClick={handleClearCache} disabled={disabled}>
          {deleteUrlsState.error ? "Error Try Again" : "Clear cache"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteUrlsModal;
